import { 
  Chart, 
  BarController, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  ChartConfiguration
} from 'chart.js';

// Register the components
Chart.register(
  BarController, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend
);

/**
 * Initialize charts when DOM is loaded
 */
document.addEventListener('DOMContentLoaded', () => {
  initializeCharts();
});

/**
 * Initialize charts after Turbo navigation events
 */
document.addEventListener('turbo:load', () => {
  initializeCharts();
});

/**
 * Theme colors that match the application's color scheme
 */
const themeColors = {
  // Primary theme colors
  theme500: 'rgba(78, 201, 196, 0.5)',  // theme-500 with opacity
  theme600: 'rgba(151, 222, 220, 0.7)',  // theme-600 with opacity
  theme700: 'rgba(54, 168, 163, 0.8)',  // theme-700 with opacity
  theme800: 'rgba(45, 138, 134, 0.9)',  // theme-800 with opacity
  
  // Border colors
  theme500Border: 'rgba(78, 201, 196, 1)',
  theme700Border: 'rgba(54, 168, 163, 1)',
  
  // Secondary colors
  teal500: 'rgba(78, 201, 196, 0.5)',  // teal-500 with opacity
  teal600: 'rgba(151, 222, 220, 0.7)',  // teal-600 with opacity
  teal700: 'rgba(54, 168, 163, 0.8)',  // teal-700 with opacity
  
  // Border colors for secondary
  teal500Border: 'rgba(78, 201, 196, 1)',
  teal700Border: 'rgba(54, 168, 163, 1)',
};

/**
 * Initialize all charts on the page
 */
function initializeCharts() {
  // Initialize organization summary charts
  const orgSummaryCharts = document.querySelectorAll('[data-chart="org-summary"]');
  orgSummaryCharts.forEach(chartElement => {
    if (chartElement instanceof HTMLCanvasElement) {
      createOrganizationSummaryChart(chartElement);
    }
  });

  // Initialize workflow hours stacked bar charts
  const workflowHoursCharts = document.querySelectorAll('[data-chart="workflow-hours"]');
  workflowHoursCharts.forEach(chartElement => {
    if (chartElement instanceof HTMLCanvasElement) {
      createWorkflowHoursChart(chartElement);
    }
  });

  // Initialize period comparison charts
  const comparisonCharts = document.querySelectorAll('[data-chart="period-comparison"]');
  comparisonCharts.forEach(chartElement => {
    if (chartElement instanceof HTMLCanvasElement) {
      createPeriodComparisonChart(chartElement);
    }
  });
}

/**
 * Create a chart for organization summary data
 */
function createOrganizationSummaryChart(canvasElement: HTMLCanvasElement) {
  // Get data from data attributes
  const tasksReceived = parseInt(canvasElement.dataset.tasksReceived || '0');
  const tasksCompleted = parseInt(canvasElement.dataset.tasksCompleted || '0');
  const hoursWorked = parseFloat(canvasElement.dataset.hoursWorked || '0');
  const hoursExpected = parseFloat(canvasElement.dataset.hoursExpected || '0');

  // Create chart configuration
  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels: ['Tasks', 'Hours'],
      datasets: [
        {
          label: 'Received/Expected',
          data: [tasksReceived, hoursExpected],
          backgroundColor: themeColors.theme500,
          borderColor: themeColors.theme500Border,
          borderWidth: 1
        },
        {
          label: 'Completed/Worked',
          data: [tasksCompleted, hoursWorked],
          backgroundColor: themeColors.teal500,
          borderColor: themeColors.teal500Border,
          borderWidth: 1
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          titleColor: '#6B7280', // gray-500
          bodyColor: '#111827', // gray-900
          borderColor: '#E5E7EB', // gray-200
          borderWidth: 1,
          padding: 12,
          boxPadding: 6,
          usePointStyle: true,
          callbacks: {
            labelTextColor: () => '#111827' // gray-900
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(243, 244, 246, 1)' // gray-100
          },
          ticks: {
            color: '#6B7280' // gray-500
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#6B7280' // gray-500
          }
        }
      }
    }
  };

  // Destroy existing chart if it exists
  const chartId = canvasElement.id;
  const existingChart = Chart.getChart(chartId);
  if (existingChart) {
    existingChart.destroy();
  }

  // Create new chart
  new Chart(canvasElement, config);
}

/**
 * Color palette for workflow bars
 */
const workflowColors = [
  'rgba(78, 201, 196, 0.7)',
  'rgba(54, 168, 163, 0.7)',
  'rgba(45, 138, 134, 0.7)',
  'rgba(151, 222, 220, 0.7)',
  'rgba(34, 108, 105, 0.7)',
  'rgba(100, 210, 206, 0.7)',
  'rgba(70, 180, 176, 0.7)',
  'rgba(120, 230, 226, 0.7)',
  'rgba(60, 150, 146, 0.7)',
  'rgba(90, 195, 191, 0.7)',
];

/**
 * Create a horizontal stacked bar chart showing hours per workflow, stacked by user
 */
function createWorkflowHoursChart(canvasElement: HTMLCanvasElement) {
  const rawData = canvasElement.dataset.workflows;
  if (!rawData) return;

  const workflows: Array<{ name: string; users: Array<{ name: string; hours: number }> }> = JSON.parse(rawData);

  // Collect all unique user names across workflows
  const userSet = new Set<string>();
  workflows.forEach(w => w.users.forEach(u => userSet.add(u.name)));
  const userNames = Array.from(userSet);

  // Workflow names are the bars (y-axis labels)
  const workflowNames = workflows.map(w => w.name);

  // Each user becomes a dataset (stacked segment)
  const datasets = userNames.map((userName, i) => {
    const color = workflowColors[i % workflowColors.length];
    return {
      label: userName,
      data: workflows.map(w => {
        const userEntry = w.users.find(u => u.name === userName);
        return userEntry ? userEntry.hours : 0;
      }),
      backgroundColor: color,
      borderColor: color.replace('0.7', '1'),
      borderWidth: 1
    };
  });

  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels: workflowNames,
      datasets: datasets
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: { size: 11 }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#6B7280',
          bodyColor: '#111827',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          padding: 10,
          callbacks: {
            label: (context: any) => ` ${context.dataset.label}: ${context.parsed.x.toFixed(1)}h`,
            labelTextColor: () => '#111827'
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: { display: true, text: 'Hours', color: '#6B7280' },
          grid: { color: 'rgba(243, 244, 246, 1)' },
          ticks: { color: '#6B7280' }
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { color: '#6B7280', font: { size: 11 } }
        }
      }
    }
  };

  const existingChart = Chart.getChart(canvasElement.id);
  if (existingChart) existingChart.destroy();

  new Chart(canvasElement, config);
}

/**
 * Create a grouped horizontal bar chart comparing current vs previous period per workflow
 */
function createPeriodComparisonChart(canvasElement: HTMLCanvasElement) {
  const rawData = canvasElement.dataset.comparison;
  if (!rawData) return;

  const data: Array<{ name: string; current: number; previous: number }> = JSON.parse(rawData);
  const currentLabel = canvasElement.dataset.currentLabel || 'Current';
  const previousLabel = canvasElement.dataset.previousLabel || 'Previous';

  const config: ChartConfiguration = {
    type: 'bar',
    data: {
      labels: data.map(d => d.name),
      datasets: [
        {
          label: currentLabel,
          data: data.map(d => d.current),
          backgroundColor: 'rgba(78, 201, 196, 0.7)',
          borderColor: 'rgba(78, 201, 196, 1)',
          borderWidth: 1
        },
        {
          label: previousLabel,
          data: data.map(d => d.previous),
          backgroundColor: 'rgba(209, 213, 219, 0.6)',
          borderColor: 'rgba(156, 163, 175, 1)',
          borderWidth: 1
        }
      ]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { usePointStyle: true, padding: 15, font: { size: 11 } }
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#6B7280',
          bodyColor: '#111827',
          borderColor: '#E5E7EB',
          borderWidth: 1,
          padding: 10,
          callbacks: {
            label: (context: any) => ` ${context.dataset.label}: ${context.parsed.x.toFixed(1)}h`,
            labelTextColor: () => '#111827'
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          title: { display: true, text: 'Hours', color: '#6B7280' },
          grid: { color: 'rgba(243, 244, 246, 1)' },
          ticks: { color: '#6B7280' }
        },
        y: {
          grid: { display: false },
          ticks: { color: '#6B7280', font: { size: 11 } }
        }
      }
    }
  };

  const existingChart = Chart.getChart(canvasElement.id);
  if (existingChart) existingChart.destroy();

  new Chart(canvasElement, config);
}